import React from "react";
import { Button } from "@magicbi/ui-library";
import { useModalDialog } from "../../../hooks/useModalDialog";
import { Checkbox } from "../../atoms/Checkbox";
import { RadioOption } from "../../atoms/RadioOption";
import { Textarea } from "../../atoms/Textarea";
import { DialogShell } from "../../molecules/DialogShell";
import { FEEDBACK_COPY as COPY } from "./FeedbackDialog.copy";
import type { FeedbackKind } from "./FeedbackDialog.copy";
import {
  Check,
  Counter,
  ErrorText,
  Form,
  HiddenFile,
  Hint,
  Kinds,
  Legend,
  LinkButton,
  Row,
  Thumb,
  ThumbImage,
} from "./FeedbackDialog.styles";
import type { FeedbackDialogProps } from "./FeedbackDialog.types";
import { useFeedbackDialogInternalState } from "./useFeedbackDialogInternalState";

const KINDS = Object.keys(COPY.kinds) as FeedbackKind[];

/** Send feedback: a kind, a message, an optional screenshot and page details, posted to the product team. */
export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const state = useFeedbackDialogInternalState(open, onClose);
  const dialog = useModalDialog(open, onClose, "feedback-message");
  const kind = COPY.kinds[state.kind];
  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    void state.submit();
  };
  return (
    <DialogShell
      open={open}
      size="sm"
      titleId="feedback-title"
      descriptionId="feedback-desc"
      title={COPY.title}
      description={COPY.description}
      closeLabel={COPY.close}
      {...dialog}
      onClose={onClose}
      footerEnd={
        <>
          <Button variant="secondary" onClick={onClose}>
            {COPY.cancel}
          </Button>
          <Button variant="primary-dark" loading={state.busy} disabled={state.busy} onClick={submit}>
            {kind.submit}
          </Button>
        </>
      }
    >
      <Form onSubmit={submit} onPaste={state.onPaste} noValidate>
        <Kinds>
          <Legend>{COPY.kindLegend}</Legend>
          {KINDS.map((id) => (
            <RadioOption key={id} name="feedback-kind" value={id} checked={state.kind === id} label={COPY.kinds[id].label} onChange={(value) => state.setKind(value as FeedbackKind)} />
          ))}
        </Kinds>
        <Textarea
          id="feedback-message"
          label={kind.messageLabel}
          required
          rows={5}
          value={state.message}
          maxLength={COPY.messageMax}
          placeholder={kind.placeholder}
          error={Boolean(state.messageError)}
          errorMessage={state.messageError}
          onChange={(event) => state.setMessage(event.target.value)}
        />
        <Counter>
          {state.message.length}/{COPY.messageMax}
        </Counter>
        <Row>
          <HiddenFile ref={state.fileRef} type="file" accept="image/png,image/jpeg,image/webp" aria-label={COPY.screenshotAdd} onChange={state.onPickFile} data-testid="feedback-file" />
          {state.screenshot ? (
            <Thumb>
              <ThumbImage src={state.screenshot.url} alt={COPY.screenshotAlt} />
              <LinkButton type="button" onClick={state.clearScreenshot}>
                {COPY.screenshotRemove}
              </LinkButton>
            </Thumb>
          ) : (
            <>
              <LinkButton type="button" onClick={() => state.fileRef.current?.click()}>
                {COPY.screenshotAdd}
              </LinkButton>
              <Hint>{COPY.screenshotHint}</Hint>
            </>
          )}
        </Row>
        {state.screenshotError ? <ErrorText role="alert">{state.screenshotError}</ErrorText> : null}
        <Check>
          <Checkbox checked={state.includeDetails} onChange={state.setIncludeDetails} ariaLabel={COPY.includeDetails} />
          {COPY.includeDetails}
        </Check>
        {state.error ? <ErrorText role="alert">{state.error}</ErrorText> : null}
      </Form>
    </DialogShell>
  );
};

export default FeedbackDialog;
